/**
 * Workflow History List View (Repository Pattern Compliant)
 *
 * Displays version history for workflow drafts with diff comparison.
 * Data-driven from backend via WorkflowDraftRepository.
 *
 * Architecture:
 * - Storage: Valkey/Redis (version snapshots)
 * - Repository: WorkflowDraftRepository.getDraftVersions()
 * - Actions: View Version, Restore, Compare
 *
 * Compliance:
 * - Repository Pattern: NO direct GraphQL calls (FERP mandate)
 * - Audit Trail: All operations logged via BaseRepository
 * - Safety Validation: Life-critical operation checks
 *
 * @see CLAUDE.md Line 24: "Repository pattern mandatory"
 * @see useWorkflowVersionHistory.ts for repository hook implementation
 */

import React from 'react';
import {
    Badge,
    Button,
    Card,
    Group,
    Stack,
    Table,
    Text,
    Title,
    ActionIcon,
    Tooltip,
    Alert,
    Loader,
    Select
} from '@mantine/core';
import {
    IconEye,
    IconArrowBackUp,
    IconGitCompare,
    IconAlertCircle,
    IconHistory
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useWorkflowVersionHistory } from '../hooks/useWorkflowVersionHistory';
import type { DraftVersion } from '../data/repositories/WorkflowDraftRepository';

interface WorkflowHistoryListViewProps {
    tenant: string;
    kind?: string;
    workflowKey?: string;
    onViewVersion?: (version: DraftVersion) => void;
    onRestoreVersion?: (version: DraftVersion) => void;
    onCompareVersions?: (version1: DraftVersion, version2: DraftVersion) => void;
}

export const WorkflowHistoryListView: React.FC<WorkflowHistoryListViewProps> = ({
    tenant,
    kind = 'WorkflowDefinition',
    workflowKey,
    onViewVersion,
    onRestoreVersion,
    onCompareVersions
}) => {
    // ✅ REPOSITORY PATTERN: Use hook that wraps WorkflowDraftRepository
    // This provides audit trails and safety validation
    const {
        versions,
        loading,
        error,
        selectedWorkflowKey,
        setSelectedWorkflowKey,
        refresh
    } = useWorkflowVersionHistory({
        kind,
        workflowKey,
        limit: 50,
        enableAutoRefresh: false,
    });

    // Format timestamp for display
    const formatTimestamp = (ms: number) => {
        return new Date(ms).toLocaleString();
    };

    // Calculate time since version
    const getTimeSince = (ms: number) => {
        const now = Date.now();
        const diff = now - ms;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        if (minutes > 0) return `${minutes}m ago`;
        return 'Just now';
    };

    const handleView = (version: DraftVersion) => {
        if (onViewVersion) {
            onViewVersion(version);
        } else {
            notifications.show({
                title: 'View Version',
                message: `Viewing version ${version.version}`,
                color: 'blue'
            });
        }
    };

    const handleRestore = (version: DraftVersion) => {
        if (onRestoreVersion) {
            onRestoreVersion(version);
        } else {
            notifications.show({
                title: 'Restore Version',
                message: `Restoring version ${version.version}`,
                color: 'orange'
            });
        }
    };

    const handleCompare = (version: DraftVersion) => {
        if (onCompareVersions && versions.length > 1) {
            // Compare with previous version
            const currentIndex = versions.findIndex(v => v.version === version.version);
            const nextVersion = currentIndex < versions.length - 1 ? versions[currentIndex + 1] : undefined;
            if (nextVersion) {
                onCompareVersions(version, nextVersion);
            }
        } else {
            notifications.show({
                title: 'Compare Versions',
                message: `Comparing version ${version.version} with previous`,
                color: 'grape'
            });
        }
    };

    if (!selectedWorkflowKey) {
        return (
            <Card withBorder>
                <Stack ta="center" p="xl">
                    <IconHistory size={48} opacity={0.3} />
                    <Title order={4}>Select a Workflow</Title>
                    <Text size="sm" c="dimmed" ta="center">
                        Choose a workflow from the list to view its version history.
                    </Text>
                    <Select
                        label="Workflow Key"
                        placeholder="Enter workflow key"
                        data={[]}
                        searchable
                        value={selectedWorkflowKey}
                        onChange={(value) => setSelectedWorkflowKey(value || '')}
                        style={{ width: '100%', maxWidth: 400 }}
                    />
                </Stack>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card withBorder>
                <Group justify="center" p="xl">
                    <Loader size="sm" />
                    <Text c="dimmed">Loading version history from Valkey...</Text>
                </Group>
            </Card>
        );
    }

    if (error) {
        return (
            <Card withBorder>
                <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error Loading History">
                    {error.message}
                </Alert>
                <Button mt="md" onClick={() => refresh()}>Retry</Button>
            </Card>
        );
    }

    if (!versions || versions.length === 0) {
        return (
            <Card withBorder>
                <Stack ta="center" p="xl">
                    <IconHistory size={48} opacity={0.3} />
                    <Title order={4}>No Version History</Title>
                    <Text size="sm" c="dimmed" ta="center">
                        No version history found for workflow: {selectedWorkflowKey}
                        <br />
                        Versions are created automatically when drafts are saved.
                    </Text>
                    <Button
                        variant="light"
                        onClick={() => setSelectedWorkflowKey('')}
                    >
                        Select Different Workflow
                    </Button>
                </Stack>
            </Card>
        );
    }

    return (
        <Card withBorder>
            <Group justify="space-between" mb="md">
                <Group>
                    <Title order={4}>Version History</Title>
                    <Badge variant="light" color="grape">
                        {selectedWorkflowKey}
                    </Badge>
                </Group>
                <Group>
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => setSelectedWorkflowKey('')}
                    >
                        Change Workflow
                    </Button>
                    <Button
                        variant="light"
                        size="xs"
                        onClick={() => refresh()}
                    >
                        Refresh
                    </Button>
                </Group>
            </Group>

            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Version</Table.Th>
                        <Table.Th>Updated</Table.Th>
                        <Table.Th>Updated By</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {versions.map((version, index) => (
                        <Table.Tr key={version.version}>
                            <Table.Td>
                                <Group gap="xs">
                                    <Text fw={500}>v{version.version}</Text>
                                    {index === 0 && (
                                        <Badge size="xs" color="blue">
                                            Latest
                                        </Badge>
                                    )}
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Group gap="xs">
                                    <Text size="sm">{formatTimestamp(version.updatedMs)}</Text>
                                    <Badge size="xs" variant="light" color="gray">
                                        {getTimeSince(version.updatedMs)}
                                    </Badge>
                                </Group>
                            </Table.Td>
                            <Table.Td>
                                <Text size="sm">{version.updatedBy}</Text>
                            </Table.Td>
                            <Table.Td>
                                <Group gap="xs" justify="flex-end">
                                    <Tooltip label="View this version">
                                        <ActionIcon
                                            variant="light"
                                            color="blue"
                                            onClick={() => handleView(version)}
                                        >
                                            <IconEye size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    {index > 0 && (
                                        <Tooltip label="Restore this version">
                                            <ActionIcon
                                                variant="light"
                                                color="orange"
                                                onClick={() => handleRestore(version)}
                                            >
                                                <IconArrowBackUp size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                    {index < versions.length - 1 && (
                                        <Tooltip label="Compare with previous version">
                                            <ActionIcon
                                                variant="light"
                                                color="grape"
                                                onClick={() => handleCompare(version)}
                                            >
                                                <IconGitCompare size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>

            <Text size="xs" c="dimmed" mt="sm">
                Showing {versions.length} version{versions.length !== 1 ? 's' : ''} for {selectedWorkflowKey} • Stored in Valkey
            </Text>
        </Card>
    );
};
